import {useEffect, useRef} from 'react';
import env from './env';
import {useMatch} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {setLoading} from '../loading';

let loadingCount = 0;

/**
 * Writes a line in the debug console each time the components re-renders, adding information about which props change caused the render.
 * (meant for internal debugging only. DO NOT USE IN PRODUCTION).
 * @param {string} name Component name used when logging the message.
 * @param {object} props props used in the component.
 */
export function useWhyDidYouUpdate(name, props) {
	// Get a mutable ref object where we can store props ...
	// ... for comparison next time this hook runs.
	const previousProps = useRef();

	useEffect(() => {
		if (!env.isProd()) {
			if (previousProps.current) {
				// Get all keys from previous and current props
				const allKeys = Object.keys({...previousProps.current, ...props});
				// Use this object to keep track of changed props
				const changesObj = {};
				// Iterate through keys
				allKeys.forEach((key) => {
					// If previous is different from current
					if (previousProps.current[key] !== props[key]) {
						// Add to changesObj
						changesObj[key] = {
							from: previousProps.current[key],
							to: props[key]
						};
					}
				});

				// If changesObj not empty then output to console
				if (Object.keys(changesObj).length) {
					console.log('[why-did-you-update]', name, changesObj);
				}
			}

			// Finally update previousProps with current props for next hook call
			previousProps.current = props;
		}
	});
}

export const useCurrentProject = () => {
	const routeParams = useMatch('/projectsummary/:projectId/*')?.params;
	const projects = useSelector((store) => {
		return store?.projectSummary?.projects?.byId;
	});

	let projectId = null;
	let projectName = null;
	let project = null;

	if (routeParams && routeParams?.projectId) {
		projectId = routeParams?.projectId;
	}

	if (projectId && projects && projectId in projects) {
		projectName = projects[projectId].projectName;
		project = projects[projectId];
	}

	return {projectName: projectName, projectId: projectId, project: project};
};

/**
 * Use this hooks along with selectArea selector will for handling loading indicators
 * @param {Array<string>} areas The loading area keys.
 * @returns {function} action dispatcher callback which accepts action param and ignoreLoading param. Pass ignoreLoading true to avoid updating loading area
 */
export const useDispatchAction = (areas = []) => {
	const dispatch = useDispatch();

	return (action, ignoreLoading = false) => {
		if (ignoreLoading) {
			return dispatch(action);
		}

		if (loadingCount === 0) {
			dispatch(setLoading({isLoading: true, areas}));
		}
		++loadingCount;
		const promise = dispatch(action);
		// The chained promise returned by `.finally()` inherits the original promise's
		// rejection. It is intentionally discarded (the caller uses `promise` below), so
		// attach a no-op `.catch` to avoid an unhandled promise rejection when the
		// dispatched action fails.
		promise
			.finally(() => {
				if (loadingCount > 0) {
					--loadingCount;
					if (loadingCount === 0) {
						dispatch(setLoading({isLoading: false, areas}));
					}
				}
			})
			.catch(() => {});
		return promise;
	};
};
